/**
 * How a production introduces itself in the sidebar — the icon, the name, and the line under it.
 *
 * 🔴 The subtitle says WHAT IT IS: kind · territory · dates. Not a uuid, and not "id: 5f2c1f9a…". A
 * producer opening a link recognises "Production · ZA · from 12 Mar 2026" and knows instantly whether
 * they are in the right show; nobody has ever recognised a uuid. Metaship's own module header does
 * exactly this ("WMS" over "Warehouse Management System") — the identity block is what tells you
 * which thing you are inside.
 *
 * Pure, so the whole decision table is unit-testable: each part is dropped when it is not known, and
 * a production about which nothing is known has no subtitle rather than a line of separators.
 */
import type { ProductionIdentity } from "./productionApi";

/**
 * The glyph for a production KIND (the API's vocabulary — services/production/production.interface.ts).
 * A kind the shell has not met falls back to the generic one, so a new kind added server-side draws
 * something sensible without a shell release.
 */
const KIND_ICONS = new Map<string, string>([
  ["slate", "view_agenda"],
  ["production", "movie"],
  ["season", "subscriptions"],
  ["block", "dashboard"],
  ["episode", "live_tv"],
]);

export const DEFAULT_PRODUCTION_ICON = "movie";

/**
 * A Map rather than an object literal: the kind arrives from the API, and an object lookup answers a
 * function for "constructor" and a string for "toString" — an inherited property drawn as an icon.
 */
export function productionIcon(kind: string | null | undefined): string {
  return (kind == null ? undefined : KIND_ICONS.get(kind)) ?? DEFAULT_PRODUCTION_ICON;
}

/** "production" → "Production". The API's kinds are single lower-case words. */
function kindLabel(kind: string | null): string | null {
  const clean = (kind ?? "").trim();
  return clean === "" ? null : clean.charAt(0).toUpperCase() + clean.slice(1);
}

/** How the subtitle renders an ISO date. Injected so the line is testable without a locale. */
export type DateFormatter = (isoDate: string) => string;

/**
 * The browser's own idea of a short date, in the shell's active locale. `Intl` is given the ISO
 * string's parts rather than `new Date(iso)`: a bare `2026-03-12` is parsed as UTC midnight, which in
 * a timezone west of Greenwich renders as the 11th — a production would appear to start the day
 * before it does.
 */
export function formatShortDate(isoDate: string, locale?: string): string {
  const [year, month, day] = isoDate.split("-").map((part) => Number(part));
  if (!year || !month || !day) return isoDate;
  const at = new Date(year, month - 1, day);
  if (Number.isNaN(at.getTime())) return isoDate;
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" }).format(at);
}

/**
 * "kykNET · Production · ZA · from 12 Mar 2026" — as much of it as is actually known.
 *
 * 🔴 WHO IT IS FOR COMES FIRST, ahead of the kind, because it is the part that tells two shows apart:
 * "the same producer will have another show with MNET, so it is important to carry that detail in the
 * production line." A subtitle is read left to right and truncated from the right.
 *
 * Territory is the `production` dimension only (D16 keeps four; the one a producer names the show by
 * is where it shoots). Several codes read "ZA/GB" rather than as separate parts, because they are one
 * fact. Null when nothing is known: an empty line under the name is worse than no line.
 */
export function productionSubtitle(
  identity: Pick<ProductionIdentity, "kind" | "territories" | "principalPhotographyFrom"> &
    Partial<Pick<ProductionIdentity, "commissionedBy" | "title" | "parentTitle">>,
  formatDate: DateFormatter = (iso) => formatShortDate(iso),
): string | null {
  const parts: string[] = [];
  if (identity.commissionedBy != null && identity.commissionedBy.trim() !== "") parts.push(identity.commissionedBy.trim());
  // 🔴 The season's own name where there is one, the kind where there is not (D143): a line reading
  // "Netflix · Season" under a node called "Season 1" told a producer nothing twice.
  const qualifier = productionQualifier(identity as Pick<ProductionIdentity, "title" | "parentTitle" | "kind">);
  if (qualifier !== null) parts.push(qualifier);
  if (identity.territories.length > 0) parts.push(identity.territories.join("/"));
  if (identity.principalPhotographyFrom !== null) parts.push(`from ${formatDate(identity.principalPhotographyFrom)}`);
  return parts.length === 0 ? null : parts.join(" · ");
}

/**
 * 🔴 **WHAT TO CALL THIS NODE, AND WHAT TO SAY UNDER IT** (D143).
 *
 * Devin, inside Season 1 after the first split: *"it should literally say Tuiskoms, and then
 * underneath that where it has Netflix · Season, that · Season should be Season 1."*
 *
 * The SHOW leads and the season qualifies, because that is the order a person recognises them in: you
 * know you are in Tuiskoms first and which season second. A node with no parent is its own show, so
 * it keeps its title and the line under it says its kind, exactly as before.
 */
export function productionHeading(identity: Pick<ProductionIdentity, "title" | "parentTitle">): string | null {
  return identity.parentTitle ?? identity.title;
}

/**
 * The season's own name, where it is inside something — "Season 1" rather than the generic "Season".
 * The kind label is what a root falls back to: "Tuiskoms · Production" says as much as there is.
 */
export function productionQualifier(identity: Pick<ProductionIdentity, "title" | "parentTitle" | "kind">): string | null {
  return identity.parentTitle === null ? kindLabel(identity.kind) : identity.title ?? kindLabel(identity.kind);
}

/** "Tuiskoms — Season 1" for the top bar, which has one line and must carry both. */
export function productionFullName(identity: Pick<ProductionIdentity, "title" | "parentTitle">): string | null {
  if (identity.parentTitle === null) return identity.title;
  return identity.title === null ? identity.parentTitle : `${identity.parentTitle} — ${identity.title}`;
}
